/**
 * Cooking Record — Prep List Fill Screen.
 *
 * Full-screen modal that shows every pending prep item as ONE row with
 * everything inline: order#, dish, planned cook time, temp input, and
 * three entry paths (manual keyboard, camera, gallery upload → OCR).
 *
 * Zero modals during service. As each row's temperature is committed it
 * creates a real hch_batch and disappears from the pending list. When a
 * row breaches the min-cook temp, an inline corrective-action field
 * appears — same rule as the CookModal.
 */
import { useState, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { X, Camera, Upload, Check, ChevronDown, AlertTriangle, SkipForward, XCircle, Search, Pencil, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Format a YYYY-MM-DD ISO date into the tenant's preferred layout.
function formatTenantDate(iso, pattern) {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  if ((pattern || "").toUpperCase() === "MM/DD/YYYY") return `${m}/${d}/${y}`;
  return `${d}/${m}/${y}`;
}

const DISPOSITION_LABEL = { served: "Serve immediately", cool_store: "Cool & store" };
const BATCH_STATUS_LABEL = {
  pending: "Pending",
  open: "In progress",
  cold_storage: "Cold Storage",
  awaiting_verification: "Awaiting verification",
  verified: "Verified",
  skipped: "Skipped",
};
const BATCH_STATUS_CLS = {
  pending: "",
  open: "",
  cold_storage: "info",
  awaiting_verification: "warn",
  verified: "ok",
  skipped: "",
};

export default function PrepListFillModal({ prepList: initial, config, canVerify, onClose, onChanged }) {
  const { tenant } = useAuth();
  const tenantDateFormat = (tenant?.operational?.date_format || "DD/MM/YYYY").toUpperCase();
  const [prepList, setPrepList] = useState(initial);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [verifying, setVerifying] = useState(false);
  const cookMin = config?.cook_min_c ?? 75;

  const items = prepList.items || [];
  const visible = useMemo(() => {
    let rows = items;
    // Filter by batch status progression. Non-completed rows always
    // stay visible under "All"; the streamlined tab set focuses the
    // operator on the states that actually need action.
    if (filter === "awaiting_verification") rows = rows.filter((i) => i.batch_status === "awaiting_verification");
    else if (filter === "verified") rows = rows.filter((i) => i.batch_status === "verified");
    else if (filter === "skipped") rows = rows.filter((i) => i.batch_status === "skipped");
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((i) =>
        (i.dish || "").toLowerCase().includes(q) ||
        (i.order_no || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [items, filter, query]);
  const countBy = (s) => items.filter((i) => i.batch_status === s).length;
  const pendingCount = countBy("pending");
  const awaitingCount = countBy("awaiting_verification");

  // Ref map so the parent can drive a "Submit all" over every pending
  // row imperatively. Each PrepRow exposes `submit()` + `hasStagedTemp()`
  // via useImperativeHandle so we don't need to lift every input's state.
  const rowRefs = useRef({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const stagedCount = items.filter(
    (i) => i.status === "pending" && rowRefs.current[i.id]?.hasStagedTemp?.(),
  ).length;

  const refresh = async () => {
    try {
      const { data } = await api.get(`/hot-cold-hold/prep-lists/${prepList.id}`);
      setPrepList(data.prep_list);
      onChanged?.(data.prep_list);
    } catch (e) { /* no-op — row-level handlers already toasted */ }
  };

  const submitAll = async () => {
    // Iterate over EVERY pending row (not just the filter/query-visible
    // ones) so an operator with a search active doesn't miss dishes.
    // Sequential — one fill call at a time — because each mutates the
    // list's `status` field and we want deterministic ordering + easy
    // error reporting.
    const pending = items.filter((i) => i.status === "pending");
    const eligible = pending.filter((i) => rowRefs.current[i.id]?.hasStagedTemp?.());
    if (!eligible.length) {
      toast.error("No dishes have a temperature entered yet.");
      return;
    }
    setBulkBusy(true);
    let ok = 0, blocked = 0, failed = 0;
    for (const it of eligible) {
      const r = rowRefs.current[it.id];
      if (!r) continue;
      const res = await r.submitSilent();
      if (res.ok) ok++;
      else if (res.blocked) blocked++;
      else failed++;
    }
    setBulkBusy(false);
    // Summary toast — success/warning/error variants combined into one
    // line so the operator sees the whole picture at a glance.
    const parts = [];
    if (ok) parts.push(`✓ ${ok} logged`);
    if (blocked) parts.push(`${blocked} need corrective action`);
    if (failed) parts.push(`${failed} failed`);
    const msg = parts.join(" · ") || "Nothing to submit.";
    if (failed || blocked) toast.error(msg);
    else toast.success(msg);
    await refresh();
  };

  return createPortal(
    <div className="fixed inset-0" style={{ background: "var(--vb-bg,#f4ede0)", zIndex: 12000, overflowY: "auto" }}
      data-testid="prep-fill-modal">
      {/* Sticky header with dish counts + close */}
      <div className="sticky top-0 z-10" style={{ background: "var(--vb-bg-2,#faf6f1)", borderBottom: "1px solid var(--vb-border)" }}>
        <div className="max-w-3xl mx-auto p-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="vb-heading text-lg m-0 truncate" style={{ color: "var(--vb-text-2)" }}>
              Prep List · {formatTenantDate(prepList.cook_date, tenantDateFormat)}
            </div>
            <div className="text-[11px] opacity-70 truncate">
              {prepList.facility_name} · {DISPOSITION_LABEL[prepList.disposition]} · {countBy("verified")}/{items.length} verified
            </div>
          </div>
          <button onClick={onClose} className="vb-btn ghost sm" data-testid="prep-fill-close">
            <X size={16} />
          </button>
        </div>
        <div className="max-w-3xl mx-auto px-3 pb-2 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="flex gap-1 overflow-x-auto flex-1" style={{ scrollbarWidth: "thin" }}>
            {[
              ["all", `All · ${items.length}`],
              ["awaiting_verification", `Awaiting · ${awaitingCount}`],
              ["verified", `Verified · ${countBy("verified")}`],
              ["skipped", `Skipped · ${countBy("skipped")}`],
            ].map(([k, label]) => (
              <button key={k}
                className={`vb-btn sm ${filter === k ? "" : "ghost"}`}
                style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
                onClick={() => setFilter(k)}
                data-testid={`prep-fill-filter-${k}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="relative sm:w-64 shrink-0">
            <Search size={13} className="absolute" style={{ left: 8, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }} />
            <input type="search" placeholder="Search dish or order #"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="vb-input"
              style={{ paddingLeft: 28, paddingRight: query ? 28 : 8 }}
              data-testid="prep-fill-search" />
            {query && (
              <button onClick={() => setQuery("")}
                className="absolute"
                style={{ right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", opacity: 0.5 }}
                data-testid="prep-fill-search-clear" aria-label="Clear search">
                <X size={13} />
              </button>
            )}
          </div>
          {pendingCount > 0 && (
            <button className="vb-btn sm shrink-0"
              onClick={submitAll} disabled={bulkBusy}
              title={stagedCount ? `Submit ${stagedCount} entered temperature${stagedCount === 1 ? "" : "s"} at once` : "Enter temperatures on the rows below first"}
              data-testid="prep-fill-submit-all">
              <Send size={13} /> {bulkBusy ? "Submitting…" : `Submit all${stagedCount ? ` (${stagedCount})` : ""}`}
            </button>
          )}
          {awaitingCount > 0 && canVerify && (
            <button className="vb-btn sm shrink-0"
              onClick={async () => {
                setVerifying(true);
                try {
                  const { data } = await api.post(`/hot-cold-hold/prep-lists/${prepList.id}/verify-all`, {});
                  toast.success(`Verified ${data.verified}${data.skipped ? ` · ${data.skipped} not yet ready` : ""}.`);
                  setPrepList(data.prep_list);
                  onChanged?.(data.prep_list);
                } catch (e) {
                  toast.error(formatErr(e.response?.data?.detail) || "Couldn't verify.");
                } finally { setVerifying(false); }
              }}
              disabled={verifying}
              data-testid="prep-fill-verify-all">
              <ShieldCheck size={13} /> {verifying ? "Verifying…" : `Verify all (${awaitingCount})`}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 space-y-2 pb-24">
        {visible.length === 0 && (
          <div className="vb-card p-6 text-center text-[13px] opacity-70" data-testid="prep-fill-empty">
            {query
              ? `No dishes match "${query}" in this bucket.`
              : filter === "pending" ? "All dishes have been logged 🎉" : "No dishes in this bucket."}
          </div>
        )}
        {visible.map((it) => (
          <PrepRow key={it.id}
            ref={(r) => { if (r) rowRefs.current[it.id] = r; else delete rowRefs.current[it.id]; }}
            item={it} prepList={prepList} cookMin={cookMin}
            onFilled={refresh} onSkipped={refresh} />
        ))}
      </div>
    </div>,
    document.body,
  );
}

function PrepRowImpl({ item, prepList, cookMin, onFilled, onSkipped }, ref) {
  const { tenant } = useAuth();
  const tenantUnit = (tenant?.operational?.temperature_unit || "C").toUpperCase();
  const isDone = item.status !== "pending";
  const [temp, setTemp] = useState("");
  const [ocr, setOcr] = useState(null);   // { photo_path, parsed_value, confidence }
  const [busy, setBusy] = useState(false);
  const [expand, setExpand] = useState(false);
  const [corrective, setCorrective] = useState("");
  const [showSkip, setShowSkip] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editOrder, setEditOrder] = useState(item.order_no || "");
  const [editDish, setEditDish] = useState(item.dish || "");
  const [editTime, setEditTime] = useState(item.planned_cook_time || "");
  const camRef = useRef();
  const upRef = useRef();

  const tempNum = temp === "" ? NaN : Number(temp);
  const breach = !Number.isNaN(tempNum) && tempNum < cookMin;

  useEffect(() => { if (breach) setExpand(true); }, [breach]);

  const runOcr = async (fileList) => {
    const f = fileList?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("context", "cooking");
      const { data } = await api.post("/ocr/temperature", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // Ambiguous unit → auto-resolve via tenant business setting so
      // we never interrupt the operator with a unit picker mid-service.
      let celsius = data?.temperature_celsius;
      if (data?.unit_ambiguous) {
        celsius = tenantUnit === "F"
          ? data.temperature_celsius_if_f
          : data.temperature_celsius_if_c;
      }
      if (celsius != null) {
        setTemp(String(celsius));
        setOcr({
          photo_path: data.image_path,
          parsed_value: celsius,
          confidence: data.confidence,
        });
        toast.success(`Read ${celsius}°C · ${data.confidence_tier || ""} confidence`);
      } else {
        toast.error(data?.retry_reason || "Couldn't read the temperature — please enter manually.");
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "OCR failed. Try again or enter manually.");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (temp === "" || Number.isNaN(tempNum)) { toast.error("Enter the temperature first."); return; }
    if (breach && !corrective.trim()) { setExpand(true); toast.error(`Under ${cookMin}°C — corrective action required.`); return; }
    setBusy(true);
    try {
      const payload = {
        temp_c: tempNum,
        corrective_action: corrective.trim(),
        cool_method: prepList.disposition === "cool_store" ? "fridge" : "fridge",
        notes: "",
      };
      if (ocr) {
        payload.ocr_photo_path = ocr.photo_path;
        payload.ocr_parsed_value = ocr.parsed_value;
        payload.ocr_confidence = ocr.confidence;
        payload.ocr_edited = Number(ocr.parsed_value) !== tempNum;
      }
      await api.post(`/hot-cold-hold/prep-lists/${prepList.id}/items/${item.id}/fill`, payload);
      toast.success(`${item.dish} logged.`);
      onFilled();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't save.");
    } finally { setBusy(false); }
  };

  // Imperative handle for the parent's "Submit all" button. Silent
  // variant — no per-row toast, returns a structured result instead so
  // the parent can render a single summary line at the end.
  useImperativeHandle(ref, () => ({
    hasStagedTemp: () => temp !== "" && !Number.isNaN(Number(temp)),
    submitSilent: async () => {
      if (temp === "" || Number.isNaN(tempNum)) return { skipped: true };
      if (breach && !corrective.trim()) { setExpand(true); return { blocked: true }; }
      try {
        const payload = {
          temp_c: tempNum,
          corrective_action: corrective.trim(),
          cool_method: "fridge",
          notes: "",
        };
        if (ocr) {
          payload.ocr_photo_path = ocr.photo_path;
          payload.ocr_parsed_value = ocr.parsed_value;
          payload.ocr_confidence = ocr.confidence;
          payload.ocr_edited = Number(ocr.parsed_value) !== tempNum;
        }
        await api.post(`/hot-cold-hold/prep-lists/${prepList.id}/items/${item.id}/fill`, payload);
        return { ok: true };
      } catch (e) {
        return { error: e };
      }
    },
  }), [temp, tempNum, breach, corrective, ocr, prepList.id, item.id]);

  const skip = async () => {
    setBusy(true);
    try {
      await api.post(`/hot-cold-hold/prep-lists/${prepList.id}/items/${item.id}/skip`, {
        reason: skipReason.trim(),
      });
      toast.success(`${item.dish} skipped.`);
      onSkipped();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't skip.");
    } finally { setBusy(false); setShowSkip(false); }
  };

  const saveEdit = async () => {
    const dish = editDish.trim();
    if (!dish) { toast.error("Dish name can't be empty."); return; }
    setBusy(true);
    try {
      await api.put(`/hot-cold-hold/prep-lists/${prepList.id}/items/${item.id}`, {
        order_no: editOrder.trim(),
        dish,
        cook_time: editTime.trim(),
      });
      toast.success("Item updated.");
      setShowEdit(false);
      onFilled();   // parent refresh — same handler works for both edit + fill
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't save changes.");
    } finally { setBusy(false); }
  };

  if (isDone) {
    // Row is either filled (with a linked hch_batch in some downstream
    // status) or skipped. We keep it visible with a status pill so the
    // operator can see the whole list at a glance — no more items
    // vanishing off the screen the moment they're saved.
    const bs = item.batch_status || item.status;
    return (
      <div className="vb-card p-3 flex items-center gap-3 text-[13px]" data-testid={`prep-fill-row-${item.id}`}>
        <div className="w-14 text-[11px] opacity-70 shrink-0">{item.order_no || "—"}</div>
        <div className="flex-1 min-w-0">
          <div className="truncate"><strong>{item.dish}</strong></div>
          <div className="text-[11px] opacity-60 flex items-center gap-2 flex-wrap">
            {item.cooked_at && <span>{new Date(item.cooked_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
            {item.has_breach && <span style={{ color: "#991b1b" }}>· NC</span>}
            {item.skipped_reason && <span>· {item.skipped_reason}</span>}
          </div>
        </div>
        <span className={`vb-pill ${BATCH_STATUS_CLS[bs] || ""} text-[10px] shrink-0`}
          data-testid={`prep-fill-row-status-${item.id}`}>
          {BATCH_STATUS_LABEL[bs] || bs}
        </span>
      </div>
    );
  }

  return (
    <div className="vb-card p-3" data-testid={`prep-fill-row-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <div className="w-14 text-[11px] opacity-70 shrink-0">{item.order_no || "—"}</div>
        <div className="flex-1 min-w-0">
          <div className="truncate text-[14px]"><strong>{item.dish}</strong></div>
          {item.planned_cook_time && (
            <div className="text-[11px] opacity-60">Time: {item.planned_cook_time}</div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <input type="number" step="0.1" inputMode="decimal"
            placeholder="°C" value={temp} onChange={(e) => setTemp(e.target.value)}
            style={{ width: 82 }} className="vb-input"
            data-testid={`prep-fill-temp-${item.id}`} />
          <input ref={camRef} type="file" accept="image/*" capture="environment"
            style={{ display: "none" }}
            onChange={(e) => runOcr(e.target.files)} />
          <input ref={upRef} type="file" accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => runOcr(e.target.files)} />
          <button className="vb-btn ghost sm" onClick={() => camRef.current?.click()}
            title="Take photo" disabled={busy}
            data-testid={`prep-fill-cam-${item.id}`}>
            <Camera size={14} />
          </button>
          <button className="vb-btn ghost sm" onClick={() => upRef.current?.click()}
            title="Upload photo" disabled={busy}
            data-testid={`prep-fill-up-${item.id}`}>
            <Upload size={14} />
          </button>
          <button className="vb-btn sm" onClick={commit}
            disabled={busy || temp === ""}
            data-testid={`prep-fill-save-${item.id}`}>
            <Check size={14} />
          </button>
          <button className="vb-btn ghost sm" onClick={() => setExpand((v) => !v)}
            title="More options"
            data-testid={`prep-fill-more-${item.id}`}>
            <ChevronDown size={14} />
          </button>
        </div>
      </div>

      {breach && !expand && (
        <div className="mt-2 text-[11px] flex items-center gap-1" style={{ color: "#991b1b" }}>
          <AlertTriangle size={12} /> Under {cookMin}°C — expand to add a corrective action.
        </div>
      )}

      {expand && (
        <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: "var(--vb-border)" }}>
          {breach && (
            <div>
              <label className="text-[11px] opacity-70">Corrective action (required — under {cookMin}°C)</label>
              <input className="vb-input" value={corrective} onChange={(e) => setCorrective(e.target.value)}
                placeholder="e.g. cooked further and re-checked at 82°C"
                data-testid={`prep-fill-corrective-${item.id}`} />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button className="vb-btn ghost sm" onClick={() => { setEditOrder(item.order_no || ""); setEditDish(item.dish || ""); setEditTime(item.planned_cook_time || ""); setShowEdit(true); }} disabled={busy}
              data-testid={`prep-fill-edit-${item.id}`}>
              <Pencil size={13} /> Edit details
            </button>
            <button className="vb-btn ghost sm" onClick={() => setShowSkip(true)} disabled={busy}
              data-testid={`prep-fill-skip-${item.id}`}>
              <SkipForward size={13} /> Skip this dish
            </button>
          </div>
        </div>
      )}

      {showEdit && (
        <div className="mt-3 p-3 rounded space-y-2" style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.3)" }}>
          <div className="text-[11px] font-semibold opacity-80">Edit item details</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] opacity-70">Order #</label>
              <input className="vb-input" value={editOrder} onChange={(e) => setEditOrder(e.target.value)}
                data-testid={`prep-fill-edit-order-${item.id}`} />
            </div>
            <div>
              <label className="text-[10px] opacity-70">Dish name</label>
              <input className="vb-input" value={editDish} onChange={(e) => setEditDish(e.target.value)}
                data-testid={`prep-fill-edit-dish-${item.id}`} />
            </div>
            <div>
              <label className="text-[10px] opacity-70">Cook time</label>
              <input className="vb-input" value={editTime} onChange={(e) => setEditTime(e.target.value)}
                data-testid={`prep-fill-edit-time-${item.id}`} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button className="vb-btn ghost sm" onClick={() => setShowEdit(false)} disabled={busy}>Cancel</button>
            <button className="vb-btn sm" onClick={saveEdit} disabled={busy}
              data-testid={`prep-fill-edit-save-${item.id}`}>Save changes</button>
          </div>
        </div>
      )}

      {showSkip && (
        <div className="mt-3 p-3 rounded" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)" }}>
          <label className="text-[11px] opacity-70">Reason (optional)</label>
          <input className="vb-input" value={skipReason} onChange={(e) => setSkipReason(e.target.value)}
            placeholder="e.g. ingredient unavailable"
            data-testid={`prep-fill-skip-reason-${item.id}`} />
          <div className="flex justify-end gap-2 mt-2">
            <button className="vb-btn ghost sm" onClick={() => setShowSkip(false)} disabled={busy}>Cancel</button>
            <button className="vb-btn sm" onClick={skip} disabled={busy}
              data-testid={`prep-fill-skip-confirm-${item.id}`}>Confirm skip</button>
          </div>
        </div>
      )}
    </div>
  );
}

const PrepRow = forwardRef(PrepRowImpl);
export { PrepRow };
